import React from 'react';
import { format } from 'date-fns';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import PDFThumbnail from '@/components/atoms/PDFThumbnail';

interface InvoiceInfoProps {
  items: {
    label: string;
    value: string | Date | string[] | React.ReactNode | undefined;
    tooltipText?: string;
    type: 'image' | 'date' | 'text';
    span: number;
  }[];
  remainingDays?: number;
}

/**
 * Helper function to determine if a URL is a PDF file
 */
const isPdfFile = (url: string): boolean => {
  return url.toLowerCase().endsWith('.pdf');
};

/**
 * Helper function to get file name from URL
 */
const getFileName = (url: string): string => {
  const parts = url.split('/');
  const fileName = parts[parts.length - 1];
  // Remove cloudinary transformation parameters if present
  return fileName.split('?')[0];
};

/**
 * Helper function to open file in new tab
 */
const openFileInNewTab = (url: string) => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

const InvoiceInfo = ({ items, remainingDays = 0 }: InvoiceInfoProps) => {
  console.log('remainingDays', remainingDays);
  console.log('items', items);

  return (
    <div className="grid grid-cols-2 gap-[14px]">
      {remainingDays && remainingDays > 0 ? (
        <header className="col-span-2 p-4 border-b-2 border-primary bg-lightPurple rounded-[4px] mb-0">
          <p className="font-medium text-sm">Draft Invoice</p>
          <p className="text-xs text-secondary leading-[100%]">
            You have {remainingDays} days remaining before the draft invoice being deleted
          </p>
        </header>
      ) : null}

      {items.map((item) => {
        if (!item.value) return null;

        if (item.label === 'Image') {
          return (
            <div key={item.label} className={`space-y-[5px] col-span-${item.span}`}>
              <label className="text-xs font-medium">{item.label}</label>
              <div className="flex gap-2 flex-wrap">
                {!item?.value || (item.value as string[]).length === 0 ? (
                  <span className="text-xs font-medium text-secondary">No image</span>
                ) : (
                  (item.value as string[])?.map((fileUrl, index) => {
                    const isPdf = isPdfFile(fileUrl);

                    if (isPdf) {
                      // Render PDF preview with first page thumbnail
                      return (
                        <PDFThumbnail
                          key={index}
                          url={fileUrl}
                          onClick={() => openFileInNewTab(fileUrl)}
                          width={68}
                          height={68}
                        />
                      );
                    } else {
                      // Render image preview
                      return (
                        <img
                          src={fileUrl}
                          key={index}
                          alt="invoice-image"
                          onClick={() => openFileInNewTab(fileUrl)}
                          className="w-[68px] h-[68px] rounded-[8px] object-cover border border-secondary cursor-pointer hover:opacity-80 transition-opacity"
                          title="Click to open image"
                        />
                      );
                    }
                  })
                )}
              </div>
            </div>
          );
        }

        return (
          <div key={item.label} className={`col-span-${item.span}`}>
            <label className="text-xs font-medium">
              <span>{item.label} </span>
              {item.tooltipText && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" className="text-primary font-bold text-xs">
                        ?
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{item.tooltipText}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </label>
            <p className="text-sm font-medium">
              {item.type === 'date' ? format(new Date(item.value as string), 'MM/dd/yyyy') : (item.value as string)}
            </p>
          </div>
        );
      })}
    </div>
  );
};

export default InvoiceInfo;
